import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import { NewRadioDropdown } from '@saucelabs/sl-component-form';
import { InlineAlert } from '@saucelabs/ds-alert';
import { MultiviewGrid, ViewSelector, Item, constants as multiviewGridConstants } from '@saucelabs/sl-component-multiview-grid';
import { Loader } from '@saucelabs/sl-component-loader';
import { OrderedMap, Set, Map } from 'immutable';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { records as appStorageRecords } from '@saucelabs/sl-module-app-storage';
import { withLaunchDarkly } from '@saucelabs/sl-util-launchdarkly';

import Styles from './MobileSelectionContainer.styles';
import ListViewHeader, { HEADER_TYPES } from '../../../components/ListViewHeader';
import {
  CONFIGURATION_DEVICE_TYPES,
  CONFIGURATION_TEST_TYPES,
  LS_PUBLIC_CLOUD_WARNING_DISMISSED,
} from '../../../constants';
import actions from '../actions';
import constants from '../constants';
import { DeviceDescriptor } from '../records';
import selectors from '../selectors';
import utils from '../utils';
import DeviceTile from './DeviceTile';
import DeviceListItem from './DeviceListItem';
import DeviceDetailModal from './DeviceDetailModal';
import DeviceSummary from './DeviceSummary';
import VmdBanner from './VmdBanner';
import {
  trackEvent,
  MANUAL_EVENTS,
  EVENTS,
  MANUAL_UI_ELEMENTS,
  LIVE_TESTING_SUBJECT_NAMES,
  LIVE_TESTING_TRACKING_PROPS,
  LIVE_TESTING_PRODUCT_SUB_AREAS,
} from '../../../tracking';
import { actions as testConfigurationActions } from '../../testConfiguration';
import * as globalActions from '../../../actions';
import SearchAndFilter from './SearchAndFilter/SearchAndFilter';
import FilterColumn from './SearchAndFilter/FilterColumn';

const context = classNames.bind(Styles);

const {
  DEVICE_SORT_MODES,
  MOBILE_SELECTION_FILTER: FILTER,
  MOBILE_SELECTION_FILTER_CATEGORY: FILTER_CATEGORY,
} = constants;

const FILTER_LABELS = Object.freeze({
  [FILTER.ANDROID]: 'Android',
  [FILTER.IOS]: 'iOS',
  [FILTER.PHONE]: 'Phones only',
  [FILTER.TABLET]: 'Tablets only',
  [FILTER.PRIVATE]: 'Private Devices',
  [FILTER.CARRIER]: 'Carrier Connectivity',
  [FILTER.COMPATIBILITY]: 'Compatible only',
});

const sortModeOptions = [{
  text: 'Preferred First',
  value: DEVICE_SORT_MODES.PREFERRED_FIRST,
}, {
  text: 'Pinned First',
  value: DEVICE_SORT_MODES.PINNED_FIRST,
},
{
  text: 'A to Z',
  value: DEVICE_SORT_MODES.A_TO_Z,
}, {
  text: 'Z to A',
  value: DEVICE_SORT_MODES.Z_TO_A,
}];

class MobileSelectionContainer extends Component {
  state = {
    activeFilters: Map(),
    modalDevice: null,
    isDeviceModalShown: false, // using a separate property to show modal since we want it to transition when closing
    searchTerm: null,
  };

  notificationData = null;

  static getDerivedStateFromProps(props, state) {
    if (
      (!props.activeFilters.equals(state.activeFilters)) ||
      (props.searchTerm !== state.searchTerm)
    ) {
      return {
        activeFilters: props.activeFilters,
        searchTerm: props.searchTerm,
      };
    }

    return null;
  }

  componentDidMount() {
    const {
      devices,
      mobileAppConfiguration,
      pollDeviceState,
      setFilters,
      startFetchingMobileRealDeviceData,
      testType,
      updateTestConfigValue,
    } = this.props;

    this.checkDeviceValidity();

    if (devices.PAID.size === 0) {
      startFetchingMobileRealDeviceData();
    } else {
      pollDeviceState();
    }

    if (testType === CONFIGURATION_TEST_TYPES.APP) {
      const filtersToBeUpdated = [
        // on by default but only for AppTesting, that's why it's not in the reducers initialState
        { category: FILTER_CATEGORY.COMPATIBILITY, filter: FILTER.COMPATIBILITY },
      ];

      const os = mobileAppConfiguration?.kind.toUpperCase(); // "os" filter needs to be in uppercase for the backend
      if (os === FILTER.ANDROID || os === FILTER.IOS) {
        filtersToBeUpdated.push({ category: FILTER_CATEGORY.OS, filter: os });
      }

      const deviceFamilies = mobileAppConfiguration?.metadata.device_family;
      if (deviceFamilies && deviceFamilies.length === 1) {
        const formFactor = deviceFamilies[0].toUpperCase();
        if (formFactor === FILTER.PHONE || formFactor === FILTER.TABLET) {
          filtersToBeUpdated.push({ category: FILTER_CATEGORY.FORM_FACTOR, filter: formFactor });
        }
      }

      setFilters(filtersToBeUpdated);
    }

    updateTestConfigValue({ deviceType: CONFIGURATION_DEVICE_TYPES.REAL_DEVICE });
  }

  componentDidUpdate(prevProps) {
    const {
      realDeviceConfiguration,
      sauceConnectPublicRdcEnabled,
      tunnel,
    } = this.props;

    if (
      prevProps.tunnel !== tunnel ||
      prevProps.realDeviceConfiguration !== realDeviceConfiguration ||
      prevProps.sauceConnectPublicRdcEnabled !== sauceConnectPublicRdcEnabled
    ) {
      this.checkDeviceValidity();
    }
  }

  componentWillUnmount() {
    const {
      clearFilters,
      stopFetchingMobileRealDeviceData,
      updateSearchTerm,
    } = this.props;

    clearFilters();
    updateSearchTerm('');
    stopFetchingMobileRealDeviceData();
  }

  trackUpgradeEvent = () => {
    trackEvent(EVENTS.OBJECT_CLICKED, {
      subject_name: LIVE_TESTING_SUBJECT_NAMES.MANUAL_FREE_TRIAL_UPGRADE,
      product_sub_area: LIVE_TESTING_PRODUCT_SUB_AREAS.CONFIG_PAGE,
      ...LIVE_TESTING_TRACKING_PROPS,
    });
  }

  validateDevice = (device) => {
    const {
      isOrgAdmin,
      sauceConnectPublicRdcEnabled,
      showSauceConnectPublicRdcDisabledNotification,
      tunnel,
    } = this.props;
    let dismissCallback = () => {};
    let publicCloudNotification;

    const sauceConnectRequested = !!tunnel;
    const publicDevice = !device.isPrivateDevice();

    if (publicDevice && sauceConnectRequested && !this.notificationData) {
      if (sauceConnectPublicRdcEnabled) {
        const warningDismissed = window.localStorage.getItem(LS_PUBLIC_CLOUD_WARNING_DISMISSED);

        if (!warningDismissed) {
          dismissCallback = this.publicCloudWarningDismissCallback;
          publicCloudNotification = {
            access: true,
            header: 'Public Cloud With Sauce Connect',
            children: (
              <p>
                {'You are launching a real device in the '}
                <span className="font-semibold">public cloud with a Sauce Connect tunnel/VPN.</span>
                {' Please be aware of the risk and use proper precautions. Read more about '}
                <a
                  href="https://docs.saucelabs.com/secure-connections/sauce-connect/setup-configuration/basic-setup/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Sauce Connect Proxy setup
                </a>
              </p>
            ),
          };
          trackEvent(MANUAL_EVENTS.SHOW_PUBLIC_CLOUD_WARNING);
        }
      } else {
        publicCloudNotification = { access: false, header: 'Sauce Connect is Disabled' };

        if (isOrgAdmin) {
          publicCloudNotification.children = (
            <p>
              {'Sauce Connect/VPN for Public Cloud is currently disabled. You can enable this feature in '}
              <a href="/team-management/settings/security">Organization&nbsp;Settings</a>
              {'.'}
            </p>
          );
        } else {
          publicCloudNotification.children = (
            <p>
              {'Sauce Connect/VPN for Public Cloud is currently disabled. To enable this feature for your team, contact your '}
              <span className="font-semibold">organization&nbsp;admin</span>
              {'.'}
            </p>
          );
        }
      }

      if (publicCloudNotification) {
        const { access, children, header } = publicCloudNotification;
        this.notificationData = showSauceConnectPublicRdcDisabledNotification({
          children,
          header,
          dismissCb: dismissCallback,
        });

        return access;
      }

      return true;
    }

    if (publicDevice &&
      sauceConnectRequested &&
      !sauceConnectPublicRdcEnabled &&
      this.notificationData) {
      return false;
    }

    return true;
  }

  publicCloudWarningDismissCallback = () => {
    window.localStorage.setItem(LS_PUBLIC_CLOUD_WARNING_DISMISSED, true);
    trackEvent(MANUAL_EVENTS.HIDE_PUBLIC_CLOUD_WARNING);
  }

  isSelectedAppTheSampleApp = () => {
    const {
      mobileAppConfiguration,
    } = this.props;

    const identifier = mobileAppConfiguration?.metadata?.identifier; // use "identifier" to define its a sample app
    return identifier?.startsWith('com.saucelabs.mydemoapp');
  }

  onLaunchClick = (device, target) => () => {
    const {
      onSubmit,
      starredRealDevices,
      testType,
      tunnel,
      url,
    } = this.props;
    this.onDeviceSelect(device); // Before launching the device, the form needs to be updated

    if (this.validateDevice(device)) {
      setTimeout(onSubmit, 0); // setTimeout is required to ensure the form is updated

      const deviceId = device?.compositeId;

      trackEvent(MANUAL_EVENTS.TEST_LAUNCHED, {
        deviceId,
        deviceType: CONFIGURATION_DEVICE_TYPES.REAL_DEVICE,
        os: device?.getPrettyOsAndOsVersion?.(),
        sauceConnectProxy: tunnel,
        isSampleApp: this.isSelectedAppTheSampleApp(),
        isStarredDevice: starredRealDevices.has(deviceId),
        testType,
        ...((testType === CONFIGURATION_TEST_TYPES.WEB) && { url }),
        target,
      });
    }
  };

  // Device summary launch click
  onLaunchClickDeviceSummary = () => {
    const {
      onSubmit,
      realDeviceConfiguration,
      starredRealDevices,
      testType,
      tunnel,
      url,
    } = this.props;

    const deviceId = realDeviceConfiguration?.compositeId;

    trackEvent(MANUAL_EVENTS.TEST_LAUNCHED, {
      deviceId,
      deviceType: CONFIGURATION_DEVICE_TYPES.REAL_DEVICE,
      isSampleApp: this.isSelectedAppTheSampleApp(),
      isStarredDevice: starredRealDevices.has(deviceId),
      os: realDeviceConfiguration?.getPrettyOsAndOsVersion?.(),
      sauceConnectProxy: tunnel,
      testType,
      ...((testType === CONFIGURATION_TEST_TYPES.WEB) && { url }),
      target: MANUAL_UI_ELEMENTS.DEVICE_SUMMARY,
    });

    onSubmit();
  }

  onDeviceSelect = (device) => {
    const { hideNotification, updateTestConfigValue } = this.props;
    updateTestConfigValue({ realDeviceConfiguration: device });

    if (device.isPrivateDevice() && this.notificationData?.payload?.id) {
      hideNotification(this.notificationData.payload.id);
      this.notificationData = null;
    }
  }

  onDeviceDeselect = () => {
    const { hideNotification, updateTestConfigValue } = this.props;
    updateTestConfigValue({ realDeviceConfiguration: null });

    if (this.notificationData?.payload?.id) {
      hideNotification(this.notificationData.payload.id);
      this.notificationData = null;
    }
  }

  showDeviceDetailModal = (device) => () => this.setState({
    modalDevice: device,
    isDeviceModalShown: true,
  }, () => {
    const { activeFilters, currentView, searchTerm } = this.props;
    const filterIsSet = activeFilters.some((filter) => filter.size);
    const searchTermExists = !!searchTerm;

    trackEvent(MANUAL_EVENTS.SHOW_DEVICE_DETAILS, {
      filterIsSet,
      searchTermExists,
      target: currentView === multiviewGridConstants.VIEW_TYPE.SMALL_BOX ?
        MANUAL_UI_ELEMENTS.DEVICE_TILE :
        MANUAL_UI_ELEMENTS.DEVICE_LIST_ITEM,
    });
  });

  hideDeviceDetailModal = () => {
    this.setState(() => ({
      isDeviceModalShown: false,
    }));
  }

  addStarredDevice = (device, target) => () => {
    const { starredRealDevices, updateStarredRealDevices } = this.props;
    const { compositeId, name: deviceName, osVersion } = device;
    trackEvent(MANUAL_EVENTS.ADD_STARRED_DEVICE, {
      deviceName,
      newStarredDeviceCount: starredRealDevices.size + 1,
      osVersion,
      target,
    });
    updateStarredRealDevices(starredRealDevices.add(compositeId), { added: device });
  }

  removeStarredDevice = (device, target) => () => {
    const { starredRealDevices, updateStarredRealDevices } = this.props;
    const { compositeId, name: deviceName, osVersion } = device;
    trackEvent(MANUAL_EVENTS.REMOVE_STARRED_DEVICE, {
      deviceName,
      newStarredDeviceCount: starredRealDevices.size - 1,
      osVersion,
      target,
    });
    updateStarredRealDevices(starredRealDevices.delete(compositeId), { removed: device });
  }

  renderDeviceItem = (device) => {
    const {
      activeFreeUser,
      availableDeviceIds,
      currentView,
      email,
      expiredUser,
      isCreditsExpired,
      isFreemiumCapable,
      isManualMinutesExpired,
      isSessionLoading,
      isUnverifiedUser,
      mobileAppConfiguration,
      realDeviceConfiguration,
      sessionAvailable,
      starredRealDevices,
      testType,
    } = this.props;
    const {
      compositeId,
      freeOfCharge,
      name,
      os,
    } = device;

    const isDeviceDisabled = utils.isDeviceDisabled(
      device, availableDeviceIds, sessionAvailable,
    );

    const hasSuitableAppConfigurationSelected = (testType === CONFIGURATION_TEST_TYPES.APP) ?
      !!mobileAppConfiguration :
      true;

    const { locked, busy } = utils.calculateLockedAndBusyStatesForRealDevice(
      activeFreeUser, expiredUser, freeOfCharge, isDeviceDisabled,
    );
    const starred = starredRealDevices.has(compositeId);
    let onStarredClick;
    const deviceIsSelected = compositeId === realDeviceConfiguration?.compositeId;
    const screenSize = `${device.screenSize}" | ${device.getPrettyResolution()}`;

    const onSelectHandler = () => {
      if (deviceIsSelected) {
        this.onDeviceDeselect();
      } else {
        this.onDeviceSelect(device);
      }
    };
    switch (currentView) {
      case multiviewGridConstants.VIEW_TYPE.SMALL_BOX:
        onStarredClick = starred ?
          this.removeStarredDevice(device, MANUAL_UI_ELEMENTS.DEVICE_TILE) :
          this.addStarredDevice(device, MANUAL_UI_ELEMENTS.DEVICE_TILE);
        return (
          <Item key={compositeId}>
            <DeviceTile
              email={email}
              hasSuitableAppConfigurationSelected={hasSuitableAppConfigurationSelected}
              image={device.getImageUrl()}
              isCreditsExpired={isCreditsExpired}
              isFreemium={isFreemiumCapable}
              isFreeTrial={device.isFreeTrialDevice()}
              isInUse={busy || isSessionLoading}
              isLocked={locked}
              isManualMinutesExpired={isManualMinutesExpired}
              name={name}
              onDetailsClick={this.showDeviceDetailModal(device)}
              onLaunchClick={this.onLaunchClick(device, MANUAL_UI_ELEMENTS.DEVICE_TILE)}
              onPinnedClick={onStarredClick}
              onSelectClick={onSelectHandler}
              os={device.getPrettyOsAndOsVersion()}
              pinned={starred}
              privateDevice={device.isPrivateDevice()}
              screenSize={screenSize}
              selected={deviceIsSelected}
              unverified={isUnverifiedUser}
            />
          </Item>
        );
      case multiviewGridConstants.VIEW_TYPE.LIST:
        onStarredClick = starred ?
          this.removeStarredDevice(device, MANUAL_UI_ELEMENTS.DEVICE_LIST_ITEM) :
          this.addStarredDevice(device, MANUAL_UI_ELEMENTS.DEVICE_LIST_ITEM);
        return (
          <Item
            key={compositeId}
            className={Styles['list-item']}
          >
            <DeviceListItem
              email={email}
              hasSuitableAppConfigurationSelected={hasSuitableAppConfigurationSelected}
              image={device.getImageUrl()}
              isCreditsExpired={isCreditsExpired}
              isFreemium={isFreemiumCapable}
              isFreeTrial={device.isFreeTrialDevice()}
              isInUse={busy || isSessionLoading}
              isLocked={locked}
              isManualMinutesExpired={isManualMinutesExpired}
              name={name}
              onDetailsClick={this.showDeviceDetailModal(device)}
              onLaunchClick={this.onLaunchClick(device, MANUAL_UI_ELEMENTS.DEVICE_LIST_ITEM)}
              onPinnedClick={onStarredClick}
              onSelectClick={onSelectHandler}
              os={device.getPrettyOsAndOsVersion()}
              osType={os === 'IOS' ? constants.OS_TYPES.IOS : constants.OS_TYPES.ANDROID} // TODO: Fix logic (use constants)
              pinned={starred}
              privateDevice={device.isPrivateDevice()}
              screenSize={screenSize}
              selected={deviceIsSelected}
              unverified={isUnverifiedUser}
            />
          </Item>
        );
      // NOTE: LARGE_BOX is currently a placeholder for LT-718 and not selectable in the UI
      case multiviewGridConstants.VIEW_TYPE.LARGE_BOX:
        return (
          <Item key={compositeId}>
            <div>{name}</div>
          </Item>
        );
      default:
        return null;
    }
  }

  renderCheckboxFilterColumn = ({
    category,
    headline,
    headlineIcon = null,
  }) => {
    const {
      activeFilters,
      currentFacets,
      mobileAppConfiguration,
      testType,
      updateFilter,
    } = this.props;

    // we don't want to display filters that are unavailable for a selected OS
    const activePlatformFilter = activeFilters.get(FILTER_CATEGORY.OS);
    // show "all" options if no or all platforms are selected
    let facetsForActivePlatform = currentFacets?.ALL || {};

    // only show options for the selected platform
    if (activePlatformFilter.size === 1) {
      facetsForActivePlatform = currentFacets?.[activePlatformFilter.first()] || {};
    }

    const availableFacets = facetsForActivePlatform?.[category] || [];
    const filteredFacets = currentFacets?.ALL?.[category] || [];
    const platformCategoryDisabled = (testType === CONFIGURATION_TEST_TYPES.APP) &&
      (category === FILTER_CATEGORY.OS) && mobileAppConfiguration;
    const moreOptionAvailbale = availableFacets.length > 1;

    const filterMap = Object.entries(availableFacets).reduce((acc, [, value]) => (
      acc.set(value, {
        active: activeFilters.get(category).includes(value) || !moreOptionAvailbale,
        disabled: platformCategoryDisabled || !moreOptionAvailbale,
        greyedOut: platformCategoryDisabled ||
          !moreOptionAvailbale ||
          !filteredFacets.includes(value),
        label: FILTER_LABELS[value],
      })
    ), OrderedMap());

    return (
      <FilterColumn
        filterCategory={category}
        headline={headline}
        headlineIcon={headlineIcon}
        items={filterMap}
        layout="checkbox"
        size="m"
        toggleItem={(filter) => {
          updateFilter({
            category,
            filter,
          });
        }}
      />
    );
  }

  // eslint-disable-next-line consistent-return
  toggleSwitchFilter = (filter) => {
    const { updateFilter } = this.props;

    switch (filter) {
      case FILTER.CARRIER:
        updateFilter({ category: FILTER_CATEGORY.NETWORK_TYPE, filter });
        break;
      case FILTER.PRIVATE:
        updateFilter({ category: FILTER_CATEGORY.CLOUD_TYPE, filter });
        break;
      case FILTER.PHONE:
      case FILTER.TABLET:
        updateFilter({ category: FILTER_CATEGORY.FORM_FACTOR, filter });
        break;
      case FILTER.COMPATIBILITY:
        updateFilter({ category: FILTER_CATEGORY.COMPATIBILITY, filter });
        break;
      default:
        break;
    }
  }

  renderSwitchFilters = () => {
    const {
      activeFilterCarrier,
      activeFilterCompatibility,
      activeFilterPhone,
      activeFilterPrivate,
      activeFilterTablet,
      currentFacets,
      testType,
    } = this.props;

    return (
      <FilterColumn
        filterCategory="type"
        headline="Type:"
        items={OrderedMap({
          [FILTER.PHONE]: {
            active: activeFilterPhone,
            disabled: !currentFacets?.ALL[FILTER_CATEGORY.FORM_FACTOR].includes(FILTER.PHONE),
            greyedOut: !currentFacets?.ALL[FILTER_CATEGORY.FORM_FACTOR].includes(FILTER.PHONE),
            label: FILTER_LABELS[FILTER.PHONE],
          },
          [FILTER.TABLET]: {
            active: activeFilterTablet,
            disabled: !currentFacets?.ALL[FILTER_CATEGORY.FORM_FACTOR].includes(FILTER.TABLET),
            greyedOut: !currentFacets?.ALL[FILTER_CATEGORY.FORM_FACTOR].includes(FILTER.TABLET),
            label: FILTER_LABELS[FILTER.TABLET],
          },
          [FILTER.PRIVATE]: {
            active: activeFilterPrivate,
            disabled: !currentFacets?.ALL[FILTER_CATEGORY.CLOUD_TYPE].includes(FILTER.PRIVATE),
            greyedOut: !currentFacets?.ALL[FILTER_CATEGORY.CLOUD_TYPE].includes(FILTER.PRIVATE),
            label: FILTER_LABELS[FILTER.PRIVATE],
          },
          [FILTER.CARRIER]: {
            active: activeFilterCarrier,
            disabled: !currentFacets?.ALL[FILTER_CATEGORY.NETWORK_TYPE].includes(FILTER.CARRIER),
            greyedOut: !currentFacets?.ALL[FILTER_CATEGORY.NETWORK_TYPE].includes(FILTER.CARRIER),
            label: FILTER_LABELS[FILTER.CARRIER],
          },
          [FILTER.COMPATIBILITY]: (testType === CONFIGURATION_TEST_TYPES.APP) ? {
            active: activeFilterCompatibility,
            disabled: false,
            greyedOut: false,
            label: FILTER_LABELS[FILTER.COMPATIBILITY],
          } : null,
        }).filter(Boolean)}
        layout="switch"
        size="m"
        toggleItem={this.toggleSwitchFilter}
      />
    );
  }

  sortDevices = (sortMode) => {
    const { sortDevices } = this.props;
    sortDevices({ sortMode });
  }

  renderGridDropdowns = () => {
    const { sortMode } = this.props;
    return (
      <div className={context('settings-item', 'leveled-form-element')}>
        <NewRadioDropdown
          initialValue={sortMode}
          input={{ onChange: this.sortDevices }}
          label="Sort by:"
          name="mobile-selection-sort-mode"
          options={sortModeOptions}
        />
      </div>
    );
  }

  handleViewTypeChange = (newViewType) => {
    const {
      currentView,
      onChangeView,
    } = this.props;
    onChangeView(newViewType);
    trackEvent(MANUAL_EVENTS.RDC_GRID_VIEW_TYPE_CHANGED, {
      prevView: currentView,
      newView: newViewType,
    });
  };

  // Validate/adjust the real device / tunnel configuration based on the RDC settings
  checkDeviceValidity() {
    const {
      realDeviceConfiguration,
      sauceConnectPublicRdcEnabled,
      updateTestConfigValue,
    } = this.props;

    // Don't check validity if this info is not available yet
    if (sauceConnectPublicRdcEnabled === null) {
      return;
    }

    if (realDeviceConfiguration) {
      const valid = this.validateDevice(realDeviceConfiguration);
      if (!valid) {
        updateTestConfigValue({ realDeviceConfiguration: null });
      }
    }
  }

  render() {
    const {
      activeFilters,
      activeFreeUser,
      allowedUsersRdcConcurrency,
      availableDeviceIds,
      clearFilters,
      currentView,
      devices,
      email,
      expiredUser,
      isCreditsExpired,
      isFreemiumCapable,
      isFreemiumUser,
      isManualMinutesExpired,
      isRdcEnabled,
      isSessionLoading,
      isUnverifiedUser,
      loading,
      mobileAppConfiguration,
      realDeviceConfiguration,
      sessionAvailabilityLoaded,
      sessionAvailable,
      starredRealDevices,
      testType,
      updateSearchTerm,
    } = this.props;
    const {
      isDeviceModalShown,
      modalDevice,
    } = this.state;

    const isModalDeviceDisabled = utils.isDeviceDisabled(
      modalDevice, availableDeviceIds, sessionAvailable,
    );
    const { locked, busy } = utils.calculateLockedAndBusyStatesForRealDevice(
      activeFreeUser, expiredUser, modalDevice?.freeOfCharge, isModalDeviceDisabled,
    );

    const availableDevicesCount = devices.PAID.count((device) => (
      !utils.isDeviceDisabled(
        device, availableDeviceIds, sessionAvailable,
      )
    )) + devices.FREE.count((device) => (
      !utils.isDeviceDisabled(
        device, availableDeviceIds, sessionAvailable,
      )
    ));
    const isAndroidOnly = activeFilters.get(FILTER_CATEGORY.OS).equals(Set([FILTER.ANDROID]));

    if ((devices.PAID.size || devices.FREE.size) && !this.trackedRdcGridOpened) {
      trackEvent(MANUAL_EVENTS.RDC_GRID_OPENED, {
        currentGridView: currentView,
        availableDevicesCount,
        isAndroidOnly,
      });
      this.trackedRdcGridOpened = true;
    }

    // Device Summary
    const freeOfCharge = realDeviceConfiguration?.freeOfCharge;
    const compositeId = realDeviceConfiguration?.compositeId;
    const isSummaryDeviceDisabled = utils.isDeviceDisabled(
      realDeviceConfiguration, availableDeviceIds, sessionAvailable,
    );
    const {
      locked: isSummaryDeviceLocked,
      busy: isSummaryDeviceBusy,
    } = utils.calculateLockedAndBusyStatesForRealDevice(
      activeFreeUser, expiredUser, freeOfCharge, isSummaryDeviceDisabled,
    );
    const isSummaryDeviceStarred = starredRealDevices.has(compositeId);
    const hasSuitableAppConfigurationSelected = (testType === CONFIGURATION_TEST_TYPES.APP) ?
      !!mobileAppConfiguration :
      true;
    const onStarredClick = isSummaryDeviceStarred ?
      this.removeStarredDevice(realDeviceConfiguration, MANUAL_UI_ELEMENTS.DEVICE_SUMMARY) :
      this.addStarredDevice(realDeviceConfiguration, MANUAL_UI_ELEMENTS.DEVICE_SUMMARY);
    return (
      <div className="pt-2 px-2 bg-gray-50">
        <div className={Styles.default}>
          {isRdcEnabled && !loading &&
            (sessionAvailabilityLoaded && !sessionAvailable) &&
            allowedUsersRdcConcurrency > 0 && (
            <InlineAlert
              buttonProps={{
                children: 'Find a Plan',
                to: '/billing',
                variant: 'text',
              }}
              color="warning"
              data-qa="mobile-selection-rdc-concurrency-limit-reached-message"
              title={`You’ve reached your testing capacity (${allowedUsersRdcConcurrency} parallel test${allowedUsersRdcConcurrency > 1 ? 's' : ''}).`}
            >
              Try again soon or add more parallel tests to your plan.
            </InlineAlert>
          )}
          {!isRdcEnabled && (
            <InlineAlert
              buttonProps={{
                children: 'Find a Plan',
                to: '/billing',
                variant: 'text',
              }}
              color="info"
              title={isFreemiumUser ?
                'Your free account does not include real devices.' :
                'Your plan does not include real devices.'}
            >
              Upgrade to start using them.
            </InlineAlert>
          )}
          {modalDevice && (
            <DeviceDetailModal
              device={modalDevice}
              email={email}
              hasSuitableAppConfigurationSelected={hasSuitableAppConfigurationSelected}
              isCreditsExpired={isCreditsExpired}
              isFreemium={isFreemiumCapable}
              isFreeTrial={modalDevice.isFreeTrialDevice()}
              isInUse={busy || isSessionLoading}
              isLocked={locked}
              isManualMinutesExpired={isManualMinutesExpired}
              onClose={this.hideDeviceDetailModal}
              onLaunch={this.onLaunchClick(modalDevice, MANUAL_UI_ELEMENTS.DEVICE_DETAIL_MODAL)}
              pinned={starredRealDevices.has(modalDevice.compositeId)}
              show={isDeviceModalShown}
              unverified={isUnverifiedUser}
            />
          )}
          <div className={context(Styles['search-wrapper'], 'mt-3')}>
            <SearchAndFilter
              loading={loading}
              numberOfResults={devices.FREE.size + devices.PAID.size}
              onClearFilters={clearFilters}
              onSearch={(searchTerm) => {
                updateSearchTerm(searchTerm);
              }}
              searchOnTextChange
            >
              {this.renderCheckboxFilterColumn({ category: FILTER_CATEGORY.OS, headline: 'Platform:' })}
              {this.renderCheckboxFilterColumn({ category: FILTER_CATEGORY.OS_VERSION, headline: 'OS Version:' })}
              {this.renderCheckboxFilterColumn({ category: FILTER_CATEGORY.MANUFACTURER, headline: 'Brand:' })}
              {this.renderCheckboxFilterColumn({ category: FILTER_CATEGORY.RESOLUTION, headline: 'Resolution:' })}
              {this.renderSwitchFilters()}
            </SearchAndFilter>
          </div>
          <div className={context(Styles['settings-wrapper'], 'mt-3')}>
            <div className={Styles['grid-control-wrapper']}>
              {this.renderGridDropdowns()}
              <div className={Styles['view-selector-wrapper']}>
                <ViewSelector
                  availableViewTypes={[
                    multiviewGridConstants.VIEW_TYPE.SMALL_BOX,
                    multiviewGridConstants.VIEW_TYPE.LIST,
                  ]}
                  currentView={currentView}
                  onChangeView={this.handleViewTypeChange}
                />
              </div>
            </div>
          </div>
          {!loading && <VmdBanner />}
          <div className={context(Styles['grid-wrapper'], 'mt-4')}>
            {!loading && (
              <>
                {
                  !!devices.FREE.size && (
                    <MultiviewGrid
                      currentView={currentView}
                      listHeader={(
                        <ListViewHeader
                          headlines={['Device', 'OS/Version', 'Screen']}
                          type={HEADER_TYPES.REAL_DEVICE_HEADER}
                        />
                      )}
                      noScrollbar
                    >
                      {devices.FREE.map(this.renderDeviceItem)}
                    </MultiviewGrid>
                  )
                }
                {
                  activeFreeUser && (
                    <p
                      className="mt-6 mb-3 text-center"
                      data-qa="device-real-trial-text"
                    >
                      <span className="font-semibold">Your plan does not include all real devices. </span>
                      <a
                        className="font-semibold"
                        href="/billing"
                        onClick={this.trackUpgradeEvent}
                      >
                        Upgrade
                      </a>
                      {' '}
                      to start using the devices below.
                    </p>
                  )
                }
                <MultiviewGrid
                  currentView={currentView}
                  listHeader={(
                    <ListViewHeader
                      headlines={['Device', 'OS/Version', 'Screen']}
                      type={HEADER_TYPES.REAL_DEVICE_HEADER}
                    />
                  )}
                  noScrollbar
                >
                  {devices.PAID.map(this.renderDeviceItem)}
                </MultiviewGrid>
                <p className={context(Styles.info, 'mt-2')}>
                  {"Please select your device. Can't find your device listed?"}
                  {' '}
                  <a
                    href="https://forms.gle/4kNfFe26WQK8sGZL8"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    Let us know!
                  </a>
                </p>
              </>
            )}
            {loading && (
              <div className={Styles['loader-wrapper']}>
                <Loader
                  fontSize={3}
                  light-spinner={false}
                  show={loading}
                />
              </div>
            )}
          </div>
          <DeviceSummary
            busy={isSummaryDeviceBusy || isSessionLoading}
            email={email}
            hasSuitableAppConfigurationSelected={hasSuitableAppConfigurationSelected}
            isCreditsExpired={isCreditsExpired}
            isInUse={isSummaryDeviceBusy || isSessionLoading}
            isLocked={isSummaryDeviceLocked}
            isManualMinutesExpired={isManualMinutesExpired}
            onLaunchClick={this.onLaunchClickDeviceSummary}
            onPinnedClick={onStarredClick}
            pinned={isSummaryDeviceStarred}
            realDeviceConfiguration={realDeviceConfiguration}
            unverified={isUnverifiedUser}
          />
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {
  clearFilters: actions.clearMobileSelectionFilter,
  hideNotification: notificationActions.hideNotification,
  startFetchingMobileRealDeviceData: actions.startFetchingMobileRealDeviceData,
  onChangeView: actions.selectMobileSelectionCurrentView,
  onSubmit: globalActions.handleTabsFormSubmit,
  pollDeviceState: actions.pollDeviceState,
  showSauceConnectPublicRdcDisabledNotification: globalActions.showSauceConnectRdcPublicDisabled,
  sortDevices: actions.sortDevices,
  updateFilter: actions.updateMobileSelectionFilter,
  setFilters: actions.setMobileSelectionFilters,
  updateSearchTerm: actions.updateMobileSelectionSearchTerm,
  updateStarredRealDevices: actions.updateStarredRealDevices,
  updateTestConfigValue: testConfigurationActions.updateTestConfigValue,
  stopFetchingMobileRealDeviceData: actions.stopFetchingMobileRealDeviceData,
};

MobileSelectionContainer.propTypes = {
  activeFilterCarrier: PropTypes.bool.isRequired,
  activeFilterCompatibility: PropTypes.bool.isRequired,
  activeFilterPhone: PropTypes.bool.isRequired,
  activeFilterPrivate: PropTypes.bool.isRequired,
  activeFilters: ImmutablePropTypes.mapOf(ImmutablePropTypes.set).isRequired,
  activeFilterTablet: PropTypes.bool.isRequired,
  activeFreeUser: PropTypes.bool.isRequired,
  allowedUsersRdcConcurrency: PropTypes.number.isRequired,
  availableDeviceIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  clearFilters: PropTypes.func.isRequired,
  currentFacets: PropTypes.shape({
    ALL: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    [FILTER.ANDROID]: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    [FILTER.IOS]: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  }),
  currentView: PropTypes.oneOf([
    multiviewGridConstants.VIEW_TYPE.LIST,
    multiviewGridConstants.VIEW_TYPE.SMALL_BOX,
    multiviewGridConstants.VIEW_TYPE.LARGE_BOX,
  ]).isRequired,
  devices: PropTypes.shape({
    FREE: ImmutablePropTypes.list.isRequired,
    PAID: ImmutablePropTypes.list.isRequired,
  }).isRequired,
  email: PropTypes.string.isRequired,
  expiredUser: PropTypes.bool.isRequired,
  hideNotification: PropTypes.func.isRequired,
  isCreditsExpired: PropTypes.bool,
  isFreemiumCapable: PropTypes.bool.isRequired,
  isFreemiumUser: PropTypes.bool.isRequired,
  isManualMinutesExpired: PropTypes.bool,
  isOrgAdmin: PropTypes.bool.isRequired,
  isRdcEnabled: PropTypes.bool.isRequired,
  isSessionLoading: PropTypes.bool.isRequired,
  isUnverifiedUser: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  mobileAppConfiguration: PropTypes.instanceOf(appStorageRecords.AppStorageFile),
  onChangeView: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pollDeviceState: PropTypes.func.isRequired,
  realDeviceConfiguration: PropTypes.instanceOf(DeviceDescriptor),
  sauceConnectPublicRdcEnabled: PropTypes.bool,
  searchTerm: PropTypes.string.isRequired,
  sessionAvailabilityLoaded: PropTypes.bool.isRequired,
  sessionAvailable: PropTypes.bool.isRequired,
  setFilters: PropTypes.func.isRequired,
  showSauceConnectPublicRdcDisabledNotification: PropTypes.func.isRequired,
  sortDevices: PropTypes.func.isRequired,
  sortMode: PropTypes.oneOf(Object.values(DEVICE_SORT_MODES)).isRequired,
  starredRealDevices: ImmutablePropTypes.setOf(PropTypes.string).isRequired,
  startFetchingMobileRealDeviceData: PropTypes.func.isRequired,
  stopFetchingMobileRealDeviceData: PropTypes.func.isRequired,
  testType: PropTypes.oneOf([
    CONFIGURATION_TEST_TYPES.WEB,
    CONFIGURATION_TEST_TYPES.APP,
  ]).isRequired,
  tunnel: PropTypes.string,
  updateFilter: PropTypes.func.isRequired,
  updateSearchTerm: PropTypes.func.isRequired,
  updateStarredRealDevices: PropTypes.func.isRequired,
  updateTestConfigValue: PropTypes.func.isRequired,
  url: PropTypes.string,
};

MobileSelectionContainer.defaultProps = {
  currentFacets: null,
  isCreditsExpired: false,
  isManualMinutesExpired: false,
  mobileAppConfiguration: null,
  realDeviceConfiguration: null,
  sauceConnectPublicRdcEnabled: null,
  tunnel: '',
  url: '',
};

const MobileSelectionContainerWithLaunchDarkly = withLaunchDarkly()(MobileSelectionContainer);

MobileSelectionContainerWithLaunchDarkly.displayName = 'MobileSelectionContainer';

export default connect(
  selectors.mobileSelectionSelector,
  mapDispatchToProps,
)(MobileSelectionContainerWithLaunchDarkly);
export { MobileSelectionContainer, mapDispatchToProps };
